<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useVuelidate } from '@vuelidate/core';
import { required, numeric, minValue } from '@vuelidate/validators';
import Button from 'dashboard/components-next/button/Button.vue';
import ProvetAPI from 'dashboard/api/provet';

const props = defineProps({
  selectedSchedule: {
    type: Object,
    default: null,
  },
  mode: {
    type: String,
    default: 'create', // 'create' or 'edit'
  },
});

const emit = defineEmits(['close', 'success']);

const { t } = useI18n();
const isSubmitting = ref(false);
const hasTriedSubmit = ref(false);

const date = ref('');
const startTime = ref(0);
const endTime = ref(0);
const pattern = ref('---');
const selectedDay = ref('---');
const autoresponse = ref('');

const rules = {
  date: {},
  startTime: { required, numeric, minValue: minValue(0) },
  endTime: { required, numeric, minValue: minValue(0) },
};

const v$ = useVuelidate(rules, { date, startTime, endTime });

const pageTitle = computed(() => {
  return props.mode === 'edit'
    ? t('PROVET_SETTINGS.WORKING_SCHEDULES.FORM.EDIT_TITLE')
    : t('PROVET_SETTINGS.WORKING_SCHEDULES.FORM.ADD_TITLE');
});

const submitButtonLabel = computed(() => {
  return props.mode === 'edit'
    ? t('PROVET_SETTINGS.WORKING_SCHEDULES.FORM.UPDATE')
    : t('PROVET_SETTINGS.WORKING_SCHEDULES.FORM.CREATE');
});

onMounted(() => {
  if (props.selectedSchedule) {
    date.value = props.selectedSchedule.date && props.selectedSchedule.date !== '*' 
      ? props.selectedSchedule.date 
      : '';
    startTime.value = props.selectedSchedule.start_time || 0;
    endTime.value = props.selectedSchedule.end_time || 0;
    // Initialize pattern/day from DB-backed fields (modificator/weekday)
    pattern.value = props.selectedSchedule.modificator || props.selectedSchedule.pattern || '---';
    selectedDay.value = props.selectedSchedule.weekday || props.selectedSchedule.day || '---';
    autoresponse.value = props.selectedSchedule.autoresponse || '';
  }
  startTimeFormatted.value = formatTime(startTime.value);
  endTimeFormatted.value = formatTime(endTime.value);
});

const formatTime = (minutes) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
};

const parseTime = (timeString) => {
  if (!timeString) return 0;
  const parts = timeString.split(':');
  if (parts.length !== 2) return 0;
  const hours = parseInt(parts[0], 10) || 0;
  const mins = parseInt(parts[1], 10) || 0;
  return hours * 60 + mins;
};

const startTimeFormatted = ref('');
const endTimeFormatted = ref('');

const patternOptions = ['---', 'One', 'Every', 'Odd', 'Even'];
const dayOptions = ['---', 'Any', 'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const shouldShowDay = computed(() => pattern.value !== 'One');
const shouldShowDate = computed(() => pattern.value === 'One');
const isPatternSelected = computed(() => pattern.value && pattern.value !== '---');
const isDaySelected = computed(() => selectedDay.value && selectedDay.value !== '---');
const isDateSelected = computed(() => Boolean(date.value));
const showPatternError = computed(() => hasTriedSubmit.value && !isPatternSelected.value);
const showDayError = computed(() => hasTriedSubmit.value && shouldShowDay.value && !isDaySelected.value);
const showDateError = computed(() => hasTriedSubmit.value && shouldShowDate.value && !isDateSelected.value);

const updateStartTime = () => {
  startTime.value = parseTime(startTimeFormatted.value);
};

const updateEndTime = () => {
  endTime.value = parseTime(endTimeFormatted.value);
};

const onClose = () => {
  emit('close');
};

const validateEndTimeAfterStart = () => {
  if (endTime.value < startTime.value) {
    return false;
  }
  return true;
};

const submitForm = async () => {
  hasTriedSubmit.value = true;
  const isValid = await v$.value.$validate();
  if (!isValid) return;
  if (!isPatternSelected.value) return;
  if (shouldShowDate.value && !isDateSelected.value) return;
  if (shouldShowDay.value && !isDaySelected.value) return;

  if (!validateEndTimeAfterStart()) {
    useAlert(t('PROVET_SETTINGS.WORKING_SCHEDULES.FORM.END_TIME_AFTER_START'), 'error');
    return;
  }

  isSubmitting.value = true;
  try {
    const payload = {
      date: shouldShowDate.value ? (date.value || null) : null,
      start_time: startTime.value,
      end_time: endTime.value,
      modificator: pattern.value,
      weekday: shouldShowDay.value ? selectedDay.value : 'Any',
      autoresponse: autoresponse.value || null,
    };

    if (props.mode === 'edit') {
      await ProvetAPI.updateWorkingSchedule(props.selectedSchedule.id, payload);
      useAlert(t('PROVET_SETTINGS.WORKING_SCHEDULES.UPDATE.SUCCESS'));
    } else {
      await ProvetAPI.createWorkingSchedule(payload);
      useAlert(t('PROVET_SETTINGS.WORKING_SCHEDULES.CREATE.SUCCESS'));
    }
    emit('success');
    onClose();
  } catch (error) {
    const errorMessage =
      error?.response?.data?.error ||
      (props.mode === 'edit'
        ? t('PROVET_SETTINGS.WORKING_SCHEDULES.UPDATE.ERROR')
        : t('PROVET_SETTINGS.WORKING_SCHEDULES.CREATE.ERROR'));
    useAlert(errorMessage, 'error');
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header :header-title="pageTitle" />
    <form class="flex flex-wrap mx-0" @submit.prevent="submitForm">
      <div class="w-full mb-4">
        <label
          class="block text-sm font-medium mb-1"
          :class="showPatternError ? 'text-red-600' : 'text-slate-700'"
        >
          {{ $t('PROVET_SETTINGS.WORKING_SCHEDULES.FORM.PATTERN.LABEL') }}
        </label>
        <select
          v-model="pattern"
          class="block w-full px-3 py-2 pr-6 mb-0 border shadow-sm rounded-xl appearance-none bg-white bg-[url('data:image/svg+xml;utf8,<svg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 32 24%27><polygon points=%270,0 32,0 16,24%27 fill=%27%236e6f73%27/></svg>')] bg-[length:9px_6px] bg-[right_0.75rem_center] bg-no-repeat leading-6"
          :class="showPatternError ? 'border-red-500 text-red-600' : 'border-slate-200 text-slate-12'"
        >
          <option v-for="option in patternOptions" :key="option" :value="option">
            {{ option }}
          </option>
        </select>
        <p v-if="showPatternError" class="text-sm text-red-600 mt-1">
          {{ $t('PROVET_SETTINGS.WORKING_SCHEDULES.FORM.PATTERN.ERROR') }}
        </p>
      </div>

      <div v-if="shouldShowDay" class="w-full mb-4">
        <label
          class="block text-sm font-medium mb-1"
          :class="showDayError ? 'text-red-600' : 'text-slate-700'"
        >
          {{ $t('PROVET_SETTINGS.WORKING_SCHEDULES.FORM.DAY.LABEL') }}
        </label>
        <select
          v-model="selectedDay"
          class="block w-full px-3 py-2 pr-6 mb-0 border shadow-sm rounded-xl appearance-none bg-white bg-[url('data:image/svg+xml;utf8,<svg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 32 24%27><polygon points=%270,0 32,0 16,24%27 fill=%27%236e6f73%27/></svg>')] bg-[length:9px_6px] bg-[right_0.75rem_center] bg-no-repeat leading-6"
          :class="showDayError ? 'border-red-500 text-red-600' : 'border-slate-200 text-slate-12'"
        >
          <option v-for="day in dayOptions" :key="day" :value="day">
            {{ day }}
          </option>
        </select>
        <p v-if="showDayError" class="text-sm text-red-600 mt-1">
          {{ $t('PROVET_SETTINGS.WORKING_SCHEDULES.FORM.DAY.ERROR') }}
        </p>
      </div>

      <woot-input
        v-if="shouldShowDate"
        v-model="date"
        type="date"
        class="w-full"
        :class="{ error: showDateError }"
        :label="$t('PROVET_SETTINGS.WORKING_SCHEDULES.FORM.DATE.LABEL')"
        :placeholder="$t('PROVET_SETTINGS.WORKING_SCHEDULES.FORM.DATE.PLACEHOLDER')"
        :error="showDateError ? $t('PROVET_SETTINGS.WORKING_SCHEDULES.FORM.DATE.ERROR') : ''"
      />

      <woot-input
        v-model="startTimeFormatted"
        type="time"
        class="w-full"
        :class="{ error: v$.startTime.$error }"
        :label="$t('PROVET_SETTINGS.WORKING_SCHEDULES.FORM.START_TIME.LABEL')"
        :placeholder="$t('PROVET_SETTINGS.WORKING_SCHEDULES.FORM.START_TIME.PLACEHOLDER')"
        :error="v$.startTime.$error ? $t('PROVET_SETTINGS.WORKING_SCHEDULES.FORM.START_TIME.ERROR') : ''"
        @input="updateStartTime(); v$.startTime.$touch"
        @blur="updateStartTime(); v$.startTime.$touch"
      />

      <woot-input
        v-model="endTimeFormatted"
        type="time"
        class="w-full"
        :class="{ error: v$.endTime.$error }"
        :label="$t('PROVET_SETTINGS.WORKING_SCHEDULES.FORM.END_TIME.LABEL')"
        :placeholder="$t('PROVET_SETTINGS.WORKING_SCHEDULES.FORM.END_TIME.PLACEHOLDER')"
        :error="v$.endTime.$error ? $t('PROVET_SETTINGS.WORKING_SCHEDULES.FORM.END_TIME.ERROR') : ''"
        @input="updateEndTime(); v$.endTime.$touch"
        @blur="updateEndTime(); v$.endTime.$touch"
      />

      <woot-input
        v-model="autoresponse"
        type="text"
        class="w-full"
        :label="$t('PROVET_SETTINGS.WORKING_SCHEDULES.FORM.AUTORESPONSE.LABEL')"
        :placeholder="$t('PROVET_SETTINGS.WORKING_SCHEDULES.FORM.AUTORESPONSE.PLACEHOLDER')"
      />

      <div class="flex items-center justify-end w-full gap-2 px-0 py-2">
        <Button
          faded
          slate
          type="reset"
          :label="$t('PROVET_SETTINGS.WORKING_SCHEDULES.FORM.CANCEL')"
          @click.prevent="onClose"
        />
        <Button
          type="submit"
          :label="submitButtonLabel"
          :disabled="v$.$invalid || isSubmitting"
          :is-loading="isSubmitting"
        />
      </div>
    </form>
  </div>
</template>

